import { createContext, ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

export interface ConfirmOptions {
  title?: string;
  /** Texto/conteúdo do corpo do diálogo. */
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** Botão de confirmar em vermelho (ações destrutivas, ex.: excluir). */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Diálogo de confirmação no padrão do template (MUI), substituindo o window.confirm
 * do navegador. Uso: `const confirm = useConfirm(); if (!(await confirm({...}))) return;`
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({});
  const resolverRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const fechar = useCallback((resultado: boolean) => {
    setOpen(false);
    resolverRef.current?.(resultado);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onClose={() => fechar(false)} maxWidth="xs" fullWidth>
        {opts.title && <DialogTitle>{opts.title}</DialogTitle>}
        <DialogContent>
          <DialogContentText component="div">{opts.description}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => fechar(false)}>{opts.cancelText ?? 'Cancelar'}</Button>
          <Button variant="contained" color={opts.danger ? 'error' : 'primary'} onClick={() => fechar(true)} autoFocus>
            {opts.confirmText ?? 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm deve ser usado dentro de um ConfirmProvider');
  return ctx;
}
